import React, { useState } from 'react';
import { Container, Typography, TextField, Button, Box, Alert } from '@mui/material';
import Grid2 from '@mui/material/Grid';
import emailjs from 'emailjs-com';
import { motion } from 'framer-motion';
import PageHeader from '../ui/PageHeader';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitState, setSubmitState] = useState(null); // 'success' | 'error' | null
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSubmitState(null);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitState(null);

    const emailData = {
      from_name: formData.name,
      from_email: formData.email,
      message: formData.message,
    };

    emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      emailData,
      process.env.REACT_APP_EMAILJS_USER_ID
    )
      .then(() => {
        setSubmitState('success');
        setFormData({ name: '', email: '', message: '' });
        setSubmitting(false);
      })
      .catch((error) => {
        console.error('Failed to send email:', error);
        setSubmitState('error');
        setSubmitting(false);
      });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <PageHeader
        title="Contact Us"
        subtitle="We usually reply within 24 hours. Fill out the form below or reach us directly."
      />

      {/* Contact Information */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5 }}
      >
        <Grid2 container spacing={4} sx={{ mb: 4 }}>
          <Grid2 item xs={12} sm={6}>
            <Typography variant="h6" gutterBottom>
              Email
            </Typography>
            <Typography variant="body2">
              <Box
                component="a"
                href="mailto:tonysjswimming@gmail.com"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
              >
                tonysjswimming@gmail.com
              </Box>
            </Typography>
          </Grid2>
          <Grid2 item xs={12} sm={6}>
            <Typography variant="h6" gutterBottom>
              Phone
            </Typography>
            <Typography variant="body2">
              <Box
                component="a"
                href="tel:+61478082050"
                sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
              >
                +61 0478 082 050
              </Box>
            </Typography>
          </Grid2>
        </Grid2>
      </Box>

      {/* Contact Form */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}
        >
          <TextField
            label="Name"
            name="name"
            variant="outlined"
            fullWidth
            required
            value={formData.name}
            onChange={handleChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3, // Apply border radius to input
              }
            }}
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            variant="outlined"
            fullWidth
            required
            value={formData.email}
            onChange={handleChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3, // Apply border radius to input
              }
            }}
          />
          <TextField
            label="Message"
            name="message"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            required
            value={formData.message}
            onChange={handleChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
              }
            }}
          />
          {submitState === 'success' && (
            <Alert severity="success" sx={{ width: '100%' }}>
              Message sent. We'll get back to you within 24 hours.
            </Alert>
          )}
          {submitState === 'error' && (
            <Alert severity="error" sx={{ width: '100%' }}>
              Something went wrong. Please try again or email us directly at tonysjswimming@gmail.com.
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={submitting}
            sx={{
              mt: 1,
              mb: 2,
              borderRadius: '20px',
            }}
          >
            {submitting ? 'Sending…' : 'Send Message'}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default ContactUs;
